/**
 * DigestWindow.tsx — ダイジェストウィンドウ（2026-07-26）
 *
 * daylist の日付ナビ右に置く「現在の状況」ログウィンドウ。
 *   PC     : 常駐インライン表示（幅350px・常に5行表示・スクロール可）
 *   スマホ : 📋 ボタン → ドロップダウンパネル（未読バッジ付き）
 *
 * ログの種類（すべて Pusher イベント起点）:
 *   1. 本日の貸出・返却予約に関する通知のみ（顧客メッセージ・送迎）
 *   2. 予約者（顧客）の LINE 発言ログ            例) 11:33 山田 様 もうすぐ返却で店舗に着きます。
 *   3. 予約に関する送迎（ピックアップ/ドロップオフ）例) 11:37 [🚗 ドロップオフ] 車両:… 顧客名:…
 *   4. 本日に限定されない車両ステータス移動ログ    例) 12:05 8851 店舗からヤードに移動
 *
 * 保存: 各ブラウザが受信時に sys/digest/api.php へ append し、
 * event_key UNIQUE + INSERT IGNORE で先勝ち重複排除（digest_logs テーブル）。
 * 過去分は DB から日別表示・全期間検索できる。
 *
 * 仕様書: md/master_md/feature-digest-window.md
 */
import { isSpotStaff } from '../utils/staffPriority';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Paper, Typography, IconButton, TextField, InputAdornment, CircularProgress, Tooltip, Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ListAltIcon from '@mui/icons-material/ListAlt';
import dayjs from 'dayjs';
import { pusherService } from '../services/pusherService';
import { useReservationData } from '../context/ReservationDataContext';
import { getInstance } from '../config/instances';
import { callDigestApi, appendDigestLog } from '../api/digestApi';
import type { DigestLogEntry as DigestLog } from '../api/digestApi';

// ダイジェストウィンドウに「表示」するタイプ。DB には raw（全 Pusher イベント）も
// 蓄積されるが（pusherService.logRawPusherEvent）、表示はこの3種のみ
const DISPLAY_TYPES = ['message', 'transfer', 'vehicle'];

// 予約ステータス除外セット（VehiclePositionMap と同じ）
const EXCLUDED_RES_STATUSES = new Set(['cancelled', 'nonreserved', 'full', 'done', '4', '5', '6', '2']);

const TYPE_STYLE: Record<string, { color: string; bg: string }> = {
    message: { color: '#00695c', bg: '#e6f0ef' },
    transfer: { color: '#1565c0', bg: '#e8f1fb' },
    vehicle: { color: '#5d4037', bg: '#f5f1ef' },
};

interface DigestWindowProps {
    lineUsers?: any[];
    vehicleStatusList?: any[];
    /** true: 常駐インライン表示（PC）。false/未指定: ボタン+ドロップダウン（スマホ） */
    inline?: boolean;
    /** inline 時の Paper 幅（既定 350。モバイルPWA のボトムシート内では '100%' を渡す） */
    width?: number | string;
    /** ログリストの高さ px（既定 5行=135。モバイルPWA では大きめを渡す） */
    bodyHeight?: number;
    /** 顧客メッセージ行のクリックで LINE のやりとりを開く（2026-08-25 追加） */
    onOpenLineChat?: (lineUser: any, reservationId: string) => void;
}

const DigestWindow: React.FC<DigestWindowProps> = ({ lineUsers, vehicleStatusList, inline = false, width = 350, bodyHeight = 5 * 27, onOpenLineChat }) => {
    const [open, setOpen] = useState(false); // スマホ（非 inline）用
    const [logs, setLogs] = useState<DigestLog[]>([]);
    const [viewDate, setViewDate] = useState(() => dayjs().format('YYYY-MM-DD'));
    const [query, setQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false); // inline 時の検索欄トグル
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<DigestLog[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [unseen, setUnseen] = useState(0);
    const listRef = useRef<HTMLDivElement | null>(null);
    const visibleRef = useRef(inline);
    useEffect(() => { visibleRef.current = inline || open; }, [inline, open]);

    const { reservations, vehicles } = useReservationData() as any;

    // ---- 本日の貸出/返却予約（条件1のフィルタに使用）----
    const todayResMaps = useMemo(() => {
        const today = dayjs().format('YYYY-MM-DD');
        const byResId: Record<string, any> = {};
        const depByVehicle: Record<string, any> = {};
        const retByVehicle: Record<string, any> = {};
        (Array.isArray(reservations) ? reservations : []).forEach((r: any) => {
            if (EXCLUDED_RES_STATUSES.has(String(r.status))) return;
            const isDep = r.start_date && String(r.start_date).substring(0, 10) === today;
            const isRet = r.end_date && String(r.end_date).substring(0, 10) === today;
            if (!isDep && !isRet) return;
            byResId[String(r.id)] = r;
            const vid = r.vehicle_id != null ? String(r.vehicle_id) : null;
            if (vid) {
                if (isDep) depByVehicle[vid] = r;
                if (isRet) retByVehicle[vid] = r;
            }
        });
        return { byResId, depByVehicle, retByVehicle };
    }, [reservations]);
    const todayResMapsRef = useRef(todayResMaps);
    useEffect(() => { todayResMapsRef.current = todayResMaps; }, [todayResMaps]);

    // ---- lineUsers / status master / vehicles を ref で参照（購読コールバック用）----
    const lineUsersRef = useRef(lineUsers);
    useEffect(() => { lineUsersRef.current = lineUsers; }, [lineUsers]);
    const statusLabelById = useMemo(() => {
        const map: Record<number, string> = {};
        (vehicleStatusList || []).forEach((s: any) => { if (s?.id != null) map[Number(s.id)] = s.label || ''; });
        return map;
    }, [vehicleStatusList]);
    const statusMapsRef = useRef({ labelById: statusLabelById, codeById: {} as Record<number, string> });
    useEffect(() => {
        const codeById: Record<number, string> = {};
        (vehicleStatusList || []).forEach((s: any) => { if (s?.id != null) codeById[Number(s.id)] = s.code || ''; });
        statusMapsRef.current = { labelById: statusLabelById, codeById };
    }, [statusLabelById, vehicleStatusList]);
    const vehiclesRef = useRef(vehicles);
    useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);

    // ---- ログ追加（ローカル即時表示 + DB 保存。event_key で重複排除）----
    const appendLog = useCallback((entry: DigestLog) => {
        setLogs(prev => {
            if (prev.some(l => l.event_key === entry.event_key)) return prev;
            const next = [...prev, entry].sort((a, b) => a.log_time.localeCompare(b.log_time));
            return next.slice(-500);
        });
        if (!visibleRef.current) setUnseen(n => n + 1);
        // DB 保存（INSERT IGNORE。他ブラウザが先に書き込んでいたら inserted:false で無視される）
        appendDigestLog(entry);
    }, []);

    // ---- ①顧客メッセージ（LINE 受信のみ・本日の貸出/返却予約者に限定）----
    useEffect(() => {
        const unsubscribe = pusherService.subscribeLine((eventName: string, data: any) => {
            if (eventName !== 'line.message.received') return;
            if (!data?.line_user_id || !data?.content) return;
            const lu = (lineUsersRef.current || []).find((u: any) => u.line_user_id === data.line_user_id);
            if (!lu) return;
            // 本日の貸出/返却予約に紐づく顧客のみ（条件1）
            const rids: any[] = lu.reservation_ids || [];
            const linkedRes = rids
                .map((rid: any) => todayResMapsRef.current.byResId[String(rid)])
                .find(Boolean);
            if (!linkedRes) return;
            // 予約の顧客名（本名）を最優先。LINE 表示名（例: Kozüe . Arai）は最終フォールバック
            // （2026-07-26: LINE名表示になっていた問題の修正）
            const name = linkedRes.customer_name || lu.customer_name || lu.line_display_name || '不明';
            const time = data.created_at ? dayjs(data.created_at) : dayjs();
            appendLog({
                event_key: `msg:${data.line_user_id}:${data.created_at || time.toISOString()}`,
                log_time: time.format('YYYY-MM-DD HH:mm:ss'),
                type: 'message',
                text: `${name} 様 ${String(data.content).slice(0, 300)}`,
                // reservation_id も残す: ログをクリックしたとき、その予約の文脈で
                // LINE チャットを開けるようにするため（2026-08-25）
                meta: { line_user_id: data.line_user_id, reservation_id: String(linkedRes.id) },
            });
        });
        return unsubscribe;
    }, [appendLog]);

    // ---- ②③車両ステータス変化（送迎 / 一般移動）----
    // pusherService.handleVehicleUpdate が発行する ChangeRecord（旧→新の diff と
    // metadata.entityUpdatedAt 付き）を購読する。updated_at ベースの event_key なので
    // どのブラウザでも同一キーになり DB 側で重複排除できる。
    useEffect(() => {
        const handler = (e: Event) => {
            const record = (e as CustomEvent).detail;
            if (!record || record.entityType !== 'vehicle') return;
            const md = record.metadata || {};
            const statusOld = md.statusOld;
            const statusNew = md.statusNew;
            if (statusNew == null || statusOld == null || statusOld === statusNew) return; // ステータス移動のみログ
            const vid = String(record.entityId);
            const code = md.vehicleCode
                || (vehiclesRef.current && vehiclesRef.current[vid]?.vehicle_code)
                || `#${vid}`;
            const { labelById, codeById } = statusMapsRef.current;
            const newCode = codeById[Number(statusNew)] || '';
            const time = md.entityUpdatedAt ? dayjs(md.entityUpdatedAt) : dayjs();
            const keyTime = md.entityUpdatedAt || time.toISOString();

            // ③送迎: 送り中(d_o)/迎え中(p_u) への変化で、本日の返却/貸出予約が紐づく場合
            if (newCode === 'd_o' || newCode === 'p_u') {
                const linkedRes = newCode === 'd_o'
                    ? todayResMapsRef.current.retByVehicle[vid]
                    : todayResMapsRef.current.depByVehicle[vid];
                if (linkedRes) {
                    const label = newCode === 'd_o' ? 'ドロップオフ' : 'ピックアップ';
                    // 担当運転者（D.O=返却運転者 / P.U=貸出運転者）。
                    // スポットスタッフ(ID:14)はメモのアサイン名を採用（VehiclePositionMap と同ロジック）。
                    // ※ドラッグ直後の運転者モーダルで後から割当てた場合はログ時点で未定のため出ない
                    const staffRaw = newCode === 'd_o' ? linkedRes.do_staff : linkedRes.pu_staff;
                    let staffName = staffRaw?.staff_name || staffRaw?.name || '';
                    if (isSpotStaff(staffRaw)) {
                        const memo = linkedRes.detail?.memo || linkedRes.memo || '';
                        const assignLabel = newCode === 'd_o' ? '返却運転者アサイン' : '貸出運転者アサイン';
                        const m = memo.match(new RegExp(`---${assignLabel} [\\d-]+ [\\d:]+---\\n(.+?)\\n---`));
                        if (m) staffName = m[1];
                    }
                    appendLog({
                        event_key: `veh:${vid}:${keyTime}:${statusOld}-${statusNew}`,
                        log_time: time.format('YYYY-MM-DD HH:mm:ss'),
                        type: 'transfer',
                        text: `[🚗 ${label}]\n車両: ${code}\n顧客名: ${linkedRes.customer_name || '不明'}`
                            + (staffName ? `\n担当: ${staffName}` : ''),
                        meta: { vehicle_id: vid, reservation_id: String(linkedRes.id) },
                    });
                    return;
                }
            }

            // ④一般の車両移動ログ（本日に限定されない）
            const oldLabel = labelById[Number(statusOld)] || `#${statusOld}`;
            const newLabel = labelById[Number(statusNew)] || `#${statusNew}`;
            appendLog({
                event_key: `veh:${vid}:${keyTime}:${statusOld}-${statusNew}`,
                log_time: time.format('YYYY-MM-DD HH:mm:ss'),
                type: 'vehicle',
                text: `${code} ${oldLabel}から${newLabel}に移動`,
                meta: { vehicle_id: vid },
            });
        };
        window.addEventListener('pusher-change-recorded', handler);
        return () => window.removeEventListener('pusher-change-recorded', handler);
    }, [appendLog]);

    // ---- 日別ログの取得（表示中・日付変更時）----
    const fetchLogs = useCallback(async (date: string) => {
        setLoading(true);
        try {
            const data = await callDigestApi('list', { date, instance: getInstance(), types: DISPLAY_TYPES });
            if (data.success) setLogs(data.logs || []);
        } catch { /* オフライン等。ライブ分は appendLog で貯まる */ }
        finally { setLoading(false); }
    }, []);

    const visible = inline || open;
    useEffect(() => {
        if (visible) { fetchLogs(viewDate); setUnseen(0); }
    }, [visible, viewDate, fetchLogs]);

    // ---- 検索 ----
    const runSearch = useCallback(async () => {
        const q = query.trim();
        if (!q) { setSearchResults(null); return; }
        setSearching(true);
        try {
            const data = await callDigestApi('list', { q, instance: getInstance(), types: DISPLAY_TYPES });
            if (data.success) setSearchResults(data.logs || []);
        } catch { setSearchResults([]); }
        finally { setSearching(false); }
    }, [query]);

    // ---- 自動スクロール（日別表示時は最下部 = 最新へ）----
    const displayed = searchResults ?? logs;
    useEffect(() => {
        if (visible && !searchResults && listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [visible, displayed.length, searchResults]);

    const isToday = viewDate === dayjs().format('YYYY-MM-DD');
    const searchVisible = inline ? showSearch : true;

    // ---- パネル本体（inline / ドロップダウン共通） ----
    const panelBody = (
        <>
            {/* ヘッダー */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.75, py: inline ? 0.1 : 0.75, bgcolor: '#0f766e', color: '#fff' }}>
                <ListAltIcon sx={{ fontSize: inline ? 14 : 18 }} />
                <Typography sx={{ fontSize: inline ? 11 : 13, fontWeight: 700, flex: 1 }}>ダイジェスト</Typography>
                {!searchResults && (
                    <>
                        <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} onClick={() => setViewDate(dayjs(viewDate).subtract(1, 'day').format('YYYY-MM-DD'))}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>&lt;</Typography>
                        </IconButton>
                        <Typography sx={{ fontSize: 11 }}>{isToday ? '今日' : dayjs(viewDate).format('M/D')}</Typography>
                        <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={isToday} onClick={() => setViewDate(dayjs(viewDate).add(1, 'day').format('YYYY-MM-DD'))}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: isToday ? 0.4 : 1 }}>&gt;</Typography>
                        </IconButton>
                    </>
                )}
                {inline && (
                    <Tooltip title="全期間を検索">
                        <IconButton
                            size="small"
                            sx={{ color: '#fff', p: 0.25, bgcolor: showSearch ? 'rgba(255,255,255,0.2)' : 'transparent' }}
                            onClick={() => { setShowSearch(s => !s); if (showSearch) { setQuery(''); setSearchResults(null); } }}
                        >
                            <SearchIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
                {!inline && (
                    <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
                )}
            </Box>

            {/* 検索ボックス（inline 時はトグル表示） */}
            {searchVisible && (
                <Box sx={{ px: 0.75, py: 0.5, borderBottom: '1px solid #e2e8f0' }}>
                    <TextField
                        size="small" fullWidth placeholder="全期間を検索（顧客名・車両・内容）"
                        value={query}
                        autoFocus={inline}
                        onChange={(e) => { setQuery(e.target.value); if (!e.target.value.trim()) setSearchResults(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                        InputProps={{
                            sx: { fontSize: 12 },
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searching ? <CircularProgress size={14} /> : (
                                        <IconButton size="small" onClick={runSearch}><SearchIcon fontSize="small" /></IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />
                    {searchResults && (
                        <Typography sx={{ fontSize: 10, color: '#64748b', mt: 0.25 }}>
                            検索結果 {searchResults.length}件（新しい順） — クリアで日別表示に戻る
                        </Typography>
                    )}
                </Box>
            )}

            {/* ログリスト（inline: 常に5行ぶんの高さ / ドロップダウン: 最大420px） */}
            <Box
                ref={listRef}
                sx={{
                    overflowY: 'auto',
                    ...(inline ? { height: bodyHeight } : { maxHeight: 420, minHeight: 120 }),
                    px: 0.75, py: 0.5, bgcolor: '#fafaf9',
                }}
            >
                {loading && <Box sx={{ textAlign: 'center', py: 1 }}><CircularProgress size={16} /></Box>}
                {!loading && displayed.length === 0 && (
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', py: 1.5 }}>
                        {searchResults ? '該当するログがありません' : 'まだログがありません'}
                    </Typography>
                )}
                {!loading && displayed.map((l) => {
                    const style = TYPE_STYLE[l.type] || TYPE_STYLE.vehicle;
                    const time = dayjs(l.log_time);
                    const dateLabel = searchResults ? time.format('M/D ') : '';
                    // 顧客メッセージ行は、クリックでその人の LINE のやりとりを開く（2026-08-25）。
                    // 過去ログ（meta に reservation_id が無い時代のもの）でも LINE ユーザーが
                    // 見つかれば開けるよう、予約IDは line_users 側からも拾う
                    const lineUser = (l.type === 'message' && l.meta?.line_user_id)
                        ? (lineUsers || []).find((u: any) => u.line_user_id === l.meta.line_user_id)
                        : null;
                    const openChat = (onOpenLineChat && lineUser)
                        ? () => {
                            const fromMeta = l.meta?.reservation_id;
                            const fromUser = (lineUser.reservation_ids || [])
                                .map((rid: any) => String(rid))
                                .find((rid: string) => !rid.startsWith('STAFF:'));
                            onOpenLineChat(lineUser, String(fromMeta || fromUser || ''));
                        }
                        : null;
                    const body = (
                        <Box
                            onClick={openChat || undefined}
                            sx={{
                                bgcolor: style.bg, borderLeft: `3px solid ${style.color}`, borderRadius: 1,
                                px: 0.75, py: 0.3, flex: 1, minWidth: 0,
                                ...(openChat ? { cursor: 'pointer', '&:hover': { filter: 'brightness(0.96)' } } : {}),
                            }}
                        >
                            <Typography sx={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                                {l.text}
                            </Typography>
                        </Box>
                    );
                    return (
                        <Box key={l.event_key} sx={{ display: 'flex', gap: 0.75, mb: 0.4, alignItems: 'flex-start' }}>
                            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap', pt: '3px' }}>
                                {dateLabel}{time.format('HH:mm')}
                            </Typography>
                            {openChat
                                ? <Tooltip title="クリックで LINE のやりとりを開く" arrow>{body}</Tooltip>
                                : body}
                        </Box>
                    );
                })}
            </Box>
        </>
    );

    // ---- PC: 常駐インライン表示（日付ナビの右に置く。幅350px・常に5行） ----
    if (inline) {
        return (
            <Paper variant="outlined" sx={{ width, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0 }}>
                {panelBody}
            </Paper>
        );
    }

    // ---- スマホ: ボタン + ドロップダウン ----
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title="ダイジェストウィンドウ（本日の動きのログ）">
                <IconButton
                    size="small"
                    onClick={() => setOpen(o => !o)}
                    sx={{
                        bgcolor: open ? '#0f766e' : 'grey.100',
                        color: open ? '#fff' : 'text.secondary',
                        '&:hover': { bgcolor: open ? '#115e59' : 'grey.200' },
                    }}
                >
                    <Badge badgeContent={unseen} color="error" max={99}>
                        <ListAltIcon fontSize="small" />
                    </Badge>
                </IconButton>
            </Tooltip>

            {open && (
                <Paper
                    elevation={8}
                    sx={{
                        // ボタンは画面右端に配置されるため右アンカーで開く
                        position: 'absolute', top: 40, right: 0,
                        zIndex: 1500, width: 380, maxWidth: '92vw',
                        display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden',
                    }}
                >
                    {panelBody}
                </Paper>
            )}
        </Box>
    );
};

export default DigestWindow;
